import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

import { trackAnalyticsCompareToggled } from '@/modules/analytics/utils/analyticsTracking';
import { PageName } from '@/shared/types/segment';

interface ComparisonModeContextValue {
  isComparing: boolean;
  setIsComparing: (value: boolean) => void;
}

const ComparisonModeContext = createContext<ComparisonModeContextValue | null>(null);

export const ComparisonModeProvider = ({ children }: { children: ReactNode }) => {
  const [isComparing, setIsComparing] = useState(false);

  return (
    <ComparisonModeContext.Provider value={{ isComparing, setIsComparing }}>
      {children}
    </ComparisonModeContext.Provider>
  );
};

export const useComparisonMode = (): ComparisonModeContextValue => {
  const context = useContext(ComparisonModeContext);
  if (!context) {
    return { isComparing: false, setIsComparing: () => {} };
  }
  return context;
};

/**
 * Comparison mode bound to a specific analytics page, with the toggle
 * instrumented. Pages should use this instead of wiring the raw context to a
 * button so every enable/disable emits a `ToggleAnalyticsCompare` event tagged
 * with the page.
 */
export const useTrackedComparison = (page: PageName) => {
  const { isComparing, setIsComparing } = useComparisonMode();

  const toggleComparison = useCallback(() => {
    const next = !isComparing;
    trackAnalyticsCompareToggled(page, next);
    setIsComparing(next);
  }, [isComparing, setIsComparing, page]);

  return { isComparing, toggleComparison };
};
